
import { ComponentPublicInstance, defineComponent, nextTick, onMounted, onUnmounted, PropType, ref, watch } from 'vue';
import { load } from '@2gis/mapgl';
import { stationsGetMapStations } from '@/services/StationsService';
import useSimpleStore from '@/helpers/useSimpleStore';
import BlueMarkerIcon from '@/assets/map/marker-blue-shadow-icon.svg';
import RedMarkerIcon from '@/assets/map/marker-red-shadow-icon.svg';
import StationMapPopup from './StationMapPopup.vue';
import type { DynamicObjectPointerEvent, HtmlMarker, Map, Marker } from '@2gis/mapgl/types';

type MarkerData = {
  stationId?: string | null;
  loading?: boolean;
  name?: string;
  address?: string;
  prices?: {
    price: number;
    fuel: { code: string };
  }[];
};

type StationMapItem = {
  id: string;
  type?: string;
  point?: number[] | null;
};

type StationMapPopupInstance = ComponentPublicInstance<{
  close: () => void;
}>;

const BlueMarkerIconData = { icon: BlueMarkerIcon, size: [14, 14] as [number, number] };
const RedMarkerIconData = { icon: RedMarkerIcon, size: [14, 14] as [number, number] };

export default defineComponent({
  name: 'StationsMap',
  components: {
    StationMapPopup,
  },
  props: {
    center: {
      type: Array as PropType<number[] | null>,
      required: false,
      default: null,
    },
    highlightPoint: {
      type: Array as PropType<number[] | null>,
      required: false,
      default: null,
    },
    isLinkEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    stationId: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
  },
  setup(props, { expose }) {
    const mapRef = ref<HTMLElement | null>(null); // Инстанс карты
    const popupRef = ref<HTMLElement | null>(null); // Инстанс всплывающего окна при нажатие на маркер
    const stationPopupRef = ref<StationMapPopupInstance | null>(null);
    const selectedStationId = ref<string | null>(null);

    const markers: Marker[] = [];

    let mapglInstance: Awaited<ReturnType<typeof load>> | undefined;
    let map: Map | undefined;
    let popupMarker: HtmlMarker | undefined;
    let highlightMarker: Marker | undefined;

    const onPopupClick = (ev: DynamicObjectPointerEvent<Marker>) => {
      const coords = ev.targetData.getCoordinates();

      popupMarker?.setCoordinates(coords);
      map?.setCenter(coords);

      const markerData = ev.targetData.userData as { stationId?: string | null } | undefined;

      if (!markerData || !markerData.stationId) {
        return;
      }

      selectedStationId.value = markerData.stationId;
    };

    const syncMapCenter = (center: number[] | null) => {
      if (!map || !center || center.length < 2) {
        return;
      }

      map.setCenter(center);
    };

    const syncHighlightMarker = (point: number[] | null) => {
      if (!map || !mapglInstance) {
        return;
      }

      if (!point || point.length < 2) {
        if (highlightMarker) {
          highlightMarker.destroy();
          highlightMarker = undefined;
        }

        return;
      }

      const stationId = props.stationId;

      if (highlightMarker) {
        if (stationId) {
          popupMarker?.setCoordinates(point);
          highlightMarker.setCoordinates(point);
          selectedStationId.value = stationId;
        } else {
          highlightMarker.setCoordinates(point);
        }

        return;
      }

      highlightMarker = new mapglInstance.Marker(map, {
        coordinates: point,
        userData: { stationId: props.stationId } as MarkerData,
      })
        .setIcon(RedMarkerIconData)
        .on('click', onPopupClick);

      markers.push(highlightMarker);

      if (stationId) {
        popupMarker?.setCoordinates(point);
        selectedStationId.value = stationId;
      }
    };

    const { isRequestInProgress, errorMessage, errorText, execute } = useSimpleStore(async () => {
      await nextTick();

      if (!mapRef.value) {
        errorMessage.value = 'Карта-контейнер не инициализировалась';
        return;
      }

      if (!popupRef.value) {
        errorMessage.value = 'Всплывающий-контейнер не инициализировался';
        return;
      }

      mapglInstance = await load();

      map = new mapglInstance.Map(mapRef.value, {
        center: props.center && props.center.length >= 2 ? props.center : [37.64, 55.76], // координаты Москвы по умолчанию
        zoom: 8,
        key: process.env.VUE_APP_2GIS_KEY,
        lang: 'ru',
      });

      map.on('resize', () => map?.invalidateSize());
      map.on('click', () => stationPopupRef.value?.close());

      // map.setStyleById('e05ac437-fcc2-4845-ad74-b1de9ce07555'); // тёмная тема
      // создание кастомной темы = https://docs.2gis.com/en/mapgl/map-style/create

      popupMarker = new mapglInstance.HtmlMarker(map, {
        coordinates: map.getCenter(),
        html: popupRef.value,
      });

      syncHighlightMarker(props.highlightPoint);

      const stations = await stationsGetMapStations();
      const items: StationMapItem[] = stations.stationsByES.items;

      for (let i = 0; i < items.length; ++i) {
        const station = items[i];

        if (!station.point || station.point.length < 2) {
          continue;
        }

        const marker = new mapglInstance.Marker(map, {
          coordinates: [station.point[0], station.point[1]],
          userData: { stationId: station.id } as MarkerData,
        })
          .setIcon(BlueMarkerIconData)
          .on('click', onPopupClick);

        markers.push(marker);
      }

      syncMapCenter(props.center);

      requestAnimationFrame(() => {
        map?.invalidateSize();
      });
    }, 'Ошибка инициализации 2GIS карты');

    onMounted(execute);

    watch(
      () => props.center,
      (center) => {
        syncMapCenter(center);
      },
      { deep: true },
    );

    watch(
      () => props.highlightPoint,
      (point) => {
        syncHighlightMarker(point);
      },
      { deep: true },
    );

    onUnmounted(() => {
      popupMarker?.destroy();

      for (let i = 0; i < markers.length; ++i) {
        markers[i].destroy();
      }

      map?.destroy();
    });

    expose({
      selectStation: (id: string | null, coords: number[]): void => {
        selectedStationId.value = id;

        if (!id || coords.length < 2) {
          return;
        }

        popupMarker?.setCoordinates(coords);
        map?.setCenter(coords);
      },
    });

    return {
      mapRef,
      popupRef,
      stationPopupRef,
      selectedStationId,
      isRequestInProgress,
      errorMessage,
      errorText,
    };
  },
});
